// Hard-block overlay that covers the screen when a touch device is held
// in portrait below tablet width. Game is genuinely unplayable in
// portrait (joystick + buttons + HUD panels collide), so this surfaces
// a clear "rotate device" state instead of letting the player struggle.
//
// Tablets ≥ 820 px logical width skip the block — iPad portrait works.
// Listens to matchMedia change events so the overlay vanishes the
// instant the device rotates.

import { isTouchDevice } from './touch';

const STYLE_ID = 'orientation-prompt-style';
const TABLET_MIN_W = 820;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes orient-rotate-pulse {
      0%, 100% { transform: rotate(-12deg) scale(1); }
      50%      { transform: rotate(78deg) scale(1.05); }
    }
    @keyframes orient-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `;
  document.head.appendChild(s);
}

export function mountOrientationPrompt(parent: HTMLElement): { destroy: () => void } | null {
  if (!isTouchDevice()) return null;
  // Idempotency guard — main.ts mounts this early (before the entrance
  // video) so portrait phones see the rotate prompt over the video;
  // app.ts mounts it again later in boot.
  if ((window as unknown as { __smpOrientationMounted?: true }).__smpOrientationMounted) {
    return null;
  }
  (window as unknown as { __smpOrientationMounted?: true }).__smpOrientationMounted = true;

  ensureStyle();

  const root = document.createElement('div');
  root.setAttribute('aria-live', 'assertive');
  root.style.cssText = `
    position: fixed; inset: 0;
    display: none;
    align-items: center; justify-content: center;
    flex-direction: column; gap: 18px;
    background: linear-gradient(165deg, rgba(6,8,18,.97), rgba(0,0,0,.99));
    color: #e5e7eb;
    z-index: 10003;
    pointer-events: auto;
    touch-action: none;
    font: 800 14px system-ui, sans-serif;
    text-align: center;
    padding: 32px;
    animation: orient-fade-in .25s ease-out;
  `;
  root.innerHTML = `
    <div style="
      font-size: 88px; line-height: 1;
      display: flex; align-items: center; justify-content: center;
      filter: drop-shadow(0 0 12px rgba(96,165,250,.4));
    ">
      <span style="display:inline-block;animation: orient-rotate-pulse 1.6s ease-in-out infinite;">📱</span>
    </div>
    <div style="
      font: 900 22px system-ui;letter-spacing: 2px;
      color: #fafafa;text-shadow: 0 2px 12px rgba(0,0,0,.7);
    ">ROTATE YOUR DEVICE</div>
    <div style="
      font: 600 13px system-ui;color: rgba(255,255,255,.7);
      max-width: 280px;line-height: 1.5;
    ">
      Virgin vs Chad plays in landscape. Turn your phone sideways to start.
    </div>
    <div style="
      margin-top: 8px;
      font: 700 10px system-ui;letter-spacing: 1.5px;
      color: rgba(96,165,250,.85);
    ">⤴ ROTATE ⤴</div>
  `;
  parent.appendChild(root);

  const portraitMQ = matchMedia('(orientation: portrait)');
  const smallMQ = matchMedia(`(max-width: ${TABLET_MIN_W - 1}px)`);

  const refresh = () => {
    const shouldShow = portraitMQ.matches && smallMQ.matches;
    root.style.display = shouldShow ? 'flex' : 'none';
  };
  refresh();

  // matchMedia change events fire on rotation + viewport resize.
  portraitMQ.addEventListener('change', refresh);
  smallMQ.addEventListener('change', refresh);
  // orientationchange is older but more reliable on some Android browsers.
  window.addEventListener('orientationchange', refresh);
  window.addEventListener('resize', refresh);

  return {
    destroy: () => {
      root.remove();
      portraitMQ.removeEventListener('change', refresh);
      smallMQ.removeEventListener('change', refresh);
      window.removeEventListener('orientationchange', refresh);
      window.removeEventListener('resize', refresh);
    },
  };
}
